import { Box, Divider, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useTheme } from "styled-components";
import CircleDoneImage from "../../assets/images/circular-done.svg";
import CircleUndoneImage from "../../assets/images/circular-undone.svg";
import CircleCurrentImage from "../../assets/images/circular-current.svg";
import ProcessingImage from "../../assets/images/status_1.svg";
import RunningModelsImage from "../../assets/images/status_2.svg";
import VerifyingOutputImage from "../../assets/images/status_3.svg";
import ProcessingDashboard from "../../assets/images/status_4.svg";
import { useCase } from "../../contexts/CaseContext";
import Translations from "../../lang/en/pages.json";
import Minimal from "../../layouts/Minimal";
import theme from "../../theme";
import { useAppContext } from "../../contexts/AppContext";
import CaseIcon from "../../assets/icons/case.svg";

const statuses: (keyof typeof Translations.pages.stauts_page)[] = [
  "processing_documents",
  "running_models",
  "verifying_output",
  "preparing_dashboard",
];

const DashedConnector: React.FC<{ isMoving?: boolean }> = ({
  isMoving = false,
}) => {
  return (
    <Stack
      sx={{
        width: "100%",
        height: 1,
        display: "flex",
        justifyContent: "center",
        px: 2,
        boxSizing: "border-box",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          height: 2,
          background: `repeating-linear-gradient(
            to right,
            ${theme.palette.success.dark} 0px,
            ${theme.palette.success.dark} 8px,
            transparent 8px,
            transparent 16px,
            ${theme.palette.success.dark} 16px,
            ${theme.palette.success.dark} 24px,
            transparent 24px,
            transparent 36px,
            ${theme.palette.success.dark} 36px,
            ${theme.palette.success.dark} 48px,
            transparent 48px,
            transparent 64px
          )`,
          backgroundSize: "40px 100%",
          animation: isMoving ? "moveDash 1.5s linear infinite" : "",
        }}
      />
      <style>
        {`
          @keyframes moveDash {
            from {
              background-position: 0 0;
            }
            to {
              background-position: 40px 0;
            }
          }
        `}
      </style>
    </Stack>
  );
};

const LineConnector: React.FC = () => {
  return (
    <Stack
      sx={{
        width: "100%",
        height: 1,
        display: "flex",
        justifyContent: "center",
        px: 2,
        boxSizing: "border-box",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          height: 1,
          border: `1px solid ${theme.palette.success.dark}`,
        }}
      />
    </Stack>
  );
};

const imageByStep = (step: number) => {
  return {
    0: ProcessingImage,
    1: RunningModelsImage,
    2: VerifyingOutputImage,
    3: ProcessingDashboard,
  }[step];
};

const Status: React.FC = () => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const theme = useTheme();
  const { revisionMeta, caseMeta } = useCase() || {};
  const { setBreadcrumbs } = useAppContext();
  useEffect(() => {
    if (revisionMeta) {
      const index = statuses.indexOf(
        revisionMeta.status as keyof typeof Translations.pages.stauts_page
      );
      setCurrentStep(index === -1 ? 0 : index);
    }

    if (caseMeta) {
      setBreadcrumbs([
        {
          label: Translations.common.header.breadcrumb.items[0].label,
          href: "/cases",
          icon: <img src={CaseIcon} />,
        },
        {
          label: caseMeta?.name ?? "",
        },
      ]);
    }
  }, [revisionMeta, caseMeta]);

  const statusDescription = useMemo(() => {
    return (
      <Stack mt={24} spacing={1} mx={"auto"} width={"80%"} maxWidth={600}>
        <Stack alignItems={"flex-start"} spacing={1} width={"50%"}>
          <Stack
            borderBottom={"3px solid #1B5E20"}
            alignItems={"center"}
            justifyContent={"flex-start"}
            direction="row"
            spacing={1}
          >
            <Typography
              color={theme.palette.success.dark}
              fontWeight={"bold"}
              variant="h6"
              textAlign={"left"}
            >
              {currentStep + 1}/{statuses.length}{" "}
            </Typography>
            <Typography
              color={theme.palette.success.dark}
              variant="body1"
              fontWeight={"light"}
              textAlign={"left"}
            >
              {Translations.pages.stauts_page[statuses[currentStep]].label}
            </Typography>
          </Stack>
          <Divider
            sx={{
              borderBottomWidth: 3,
              borderColor: theme.palette.success.dark,
            }}
            orientation="horizontal"
          />
        </Stack>
        <Typography
          variant="caption"
          fontWeight={"light"}
          color={theme.palette.success.dark}
        >
          {Translations.pages.stauts_page[statuses[currentStep]].description}
        </Typography>
        <Box
          width={"100%"}
          display={"flex"}
          justifyContent={"center"}
          margin={"auto"}
          position={"relative"}
        >
          <Box
            zIndex={-1}
            style={{
              background: "linear-gradient(to bottom, #ACF2C8, #F9F9FF)",
            }}
            borderRadius={"50%"}
            position={"absolute"}
            top={-200}
            width={140}
            height={140}
            padding={4}
          >
            <img width={"100%"} src={imageByStep(currentStep)} />
          </Box>
        </Box>
      </Stack>
    );
  }, [currentStep]);

  const circleIconByStep = (step: number) => {
    if (step < currentStep) {
      return CircleDoneImage;
    } else if (step === currentStep) {
      return CircleCurrentImage;
    } else {
      return CircleUndoneImage;
    }
  };

  return (
    <Minimal>
      <Stack
        mt={12}
        width={"90%"}
        maxWidth={800}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {statuses.map((s, i) => (
          <React.Fragment key={s}>
            <Stack width={50} position={"relative"}>
              <Typography
                whiteSpace={"nowrap"}
                fontWeight={currentStep === i ? "medium" : "light"}
                textAlign={"center"}
                fontSize={12}
                position={"absolute"}
                top={-35}
                right={-25}
                variant="h6"
              >
                {Translations.pages.stauts_page[s].label}
              </Typography>
              <img src={circleIconByStep(i)} alt="circle" />
            </Stack>
            {i < statuses.length - 1 && i >= currentStep && (
              <DashedConnector
                isMoving={i === currentStep || i === currentStep}
              />
            )}
            {i < statuses.length - 1 && i < currentStep && <LineConnector />}
          </React.Fragment>
        ))}
      </Stack>

      {statusDescription}
    </Minimal>
  );
};

export default Status;
